import { Box, CssBaseline, ThemeProvider } from '@mui/material'
import { FC, useEffect, useRef, useState } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Route, Routes, useLocation } from 'react-router-dom'
import { AccountsClient } from './interactors/clients/AccountsClient'
import { AuthService } from './interactors/services/AuthService'
import { useGlobalLoaderStore } from './store/GlobalLoaderStore'
import { AddAnimalScreen } from './views/Animals/AddAnimalScreen'
import { CircularProgressPanel } from './views/common/CircularProgressPanel'
import { LoginPage } from './views/Login/LoginPage'
import { AddMemberScreen } from './views/Members/AddMemberScreen'
import { PATHS } from './views/PATHS'
import { theme } from './views/theme'
import { PrivateRoutes } from './views/utils/PrivateRoutes'

import { MetricalpReactProvider } from '@metricalp/react'
import { Navigate } from 'react-router-dom'
import { ErrorsClient } from './interactors/clients/ErrorClient'
import { useAccountsStore } from './store/AccountsStore'
import { useGlobalSnackbarStore } from './store/GlobalSnackBarStore'
import { useMemberAccessPolicy } from './utils/hooks/useMemberAccessPolicy'
import { ScrollProvider } from './utils/hooks/ScrollContext'
import { AddAdopterScreen } from './views/Adopters/AddAdopterScreen'
import { AdopterDetailsScreen } from './views/Adopters/AdopterDetailsScreen'
import { AdoptersListScreen } from './views/Adopters/AdoptersListScreen'
import { AssignAnimalToAdopter } from './views/Adopters/AssignAnimalToAdopter'
import { AnimalBulkEdit } from './views/Animals/AnimalBulkEdit'
import { AnimalListScreen } from './views/Animals/AnimalsListScreen'
import { AnimalDetailsScreen } from './views/Animals/Details/AnimalDetailsScreen'
import { AssociationDetailsScreen } from './views/Association/AssociationDetailsScreen'
import { AddDonatorScreen } from './views/Association/DonationTracking/AddDonatorScreen'
import { SignatureScreen } from './views/Association/EditSignatureScreen'
import { EditTemplateDocument } from './views/Association/EditTemplateDocument'
import { EditTemplateEmail } from './views/Association/EditTemplateEmail'
import { SelectZonesScreen } from './views/Association/SelectZonesScreen'
import { GlobalSnackBar } from './views/common/GlobalSnackbar'
import { AdminLoginPage } from './views/Login/AdminLoginPage'
import { MapScreen } from './views/Map/MapScreen'
import { MemberDetailsScreen } from './views/Members/MembersDetailsScreen'
import { ContactListScreen } from './views/Members/MembersListScreen'
import { VeterinariesListScreen } from './views/Members/VeterinariesListScreen'
import { ResetPasswordPage } from './views/ResetPassword/ResetPasswordPage'
import { CGUPopUp } from './views/utils/CGUPopUp'
import { CompletionScreen } from './views/Association/CompletionScreen'
import { ContentLimitsOfferModal } from './views/utils/ContentLimitsOfferModal'
import { ContentLimitsOfferStage, getContentLimitsOfferStage } from './domain/Customer/ContentLimits'
import { PaymentsTutorialModal } from './views/Payments/PaymentsTutorialModal'
import { useStripeConnectStatus } from './utils/hooks/useStripeConnectStatus'

const queryClient = new QueryClient()
const CONTENT_LIMITS_OFFER_STORAGE_KEY = 'petso_content-limits-offer'

const getContentLimitsOfferStorageKey = (customerId: string, occurrence: string, accountId?: string) => {
  return `${CONTENT_LIMITS_OFFER_STORAGE_KEY}:${customerId}:${accountId || 'no-account'}:${occurrence}`
}

const App: FC = () => {
  const membersStore = useAccountsStore()
  const location = useLocation()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const { isLoading, setIsLoading } = useGlobalLoaderStore()
  const [isCGUPopupOpen, setIsCGUPopupOpen] = useState(false)
  const [isPaymentsTutorialOpen, setIsPaymentsTutorialOpen] = useState(false)
  const [contentLimitsOfferStage, setContentLimitsOfferStage] = useState<ContentLimitsOfferStage | null>(null)
  const { policy: memberAccessPolicy } = useMemberAccessPolicy(
    membersStore.connectedAccount?.customerId,
    Boolean(membersStore.connectedAccount)
  )

  const isAdmin = membersStore.connectedAccount?.permission === 'administrator'
  const { status: stripeConnectStatus } = useStripeConnectStatus(
    Boolean(membersStore.connectedAccount?.hasAcceptedTerms)
  )

  useEffect(() => {
    if (membersStore.connectedAccount && !membersStore.connectedAccount?.hasAcceptedTerms) {
      setIsCGUPopupOpen(true)
    }
  }, [membersStore.connectedAccount])

  // Offered again every third visit to the home page, for as long as the organization has no
  // Stripe account to be paid into, so it keeps reminding without being in the way. Counted on the
  // server, so it follows the person from one browser or device to the next and cannot be reset by
  // clearing a tab. One visit per tab: the ref survives navigation within the tab and guards
  // against React's double effect in development.
  const countedHomePageLoad = useRef(false)

  useEffect(() => {
    const account = membersStore.connectedAccount
    const isHomePage = location.pathname === PATHS.animals.absolute

    if (
      !isHomePage ||
      !account?.hasAcceptedTerms ||
      account.permission !== 'administrator' ||
      stripeConnectStatus !== 'missing' ||
      countedHomePageLoad.current
    ) {
      return
    }

    countedHomePageLoad.current = true
    AccountsClient.countPaymentsTutorialHomePageLoad()
      .then((shouldOpen) => setIsPaymentsTutorialOpen(shouldOpen))
      .catch(() => undefined)
  }, [membersStore.connectedAccount, stripeConnectStatus, location.pathname])

  const closePaymentsTutorial = () => {
    setIsPaymentsTutorialOpen(false)
  }
  /*   useEffect(() => {
    if (
      membersStore.connectedAccount &&
      membersStore.connectedAccount?.hasAcceptedTerms &&
      !membersStore.connectedAccount?.hasSeenNewFeatures
    ) {
      setIsNewFeaturePopupOpen(true)
    }
  }, [membersStore.connectedAccount]) */

  useEffect(() => {
    if (!membersStore.connectedAccount || !isAdmin || !memberAccessPolicy) {
      return
    }

    // Only a free organization that is, or will be, limited: never Essential, Premium, grandfathered
    // or Stripe Connect, and nothing at all while the backend does not send `plan` and `content`.
    const stage = getContentLimitsOfferStage(memberAccessPolicy)
    if (stage == null) {
      return
    }

    // During the trial, once per reminder (60, 30, 14, 7, 3 and 1 day(s) before its end); afterwards, once.
    const reminderLevel = memberAccessPolicy.trial.reminderLevel
    if (stage === 'trial' && (reminderLevel == null || reminderLevel === 'expired')) {
      return
    }

    const storageKey = getContentLimitsOfferStorageKey(
      membersStore.connectedAccount.customerId,
      stage === 'trial' ? `trial-${reminderLevel}` : 'ended',
      membersStore.connectedAccount.id
    )
    if (localStorage.getItem(storageKey) === 'true') {
      return
    }

    localStorage.setItem(storageKey, 'true')
    setContentLimitsOfferStage(stage)
  }, [memberAccessPolicy, membersStore.connectedAccount, isAdmin])

  useEffect(() => {
    if (membersStore.connectedAccount && membersStore.connectedAccount?.permission === 'silent') {
      AuthService.logout()
      globalSnackbarStore.triggerErrorMessage(
        "Votre compte n'a pas les permissions nécessaires pour accéder à l'application. Si vous pensez que c'est une erreur, contactez un administrateur de votre association"
      )
    }
  }, [membersStore.connectedAccount])

  useEffect(() => {
    AuthService.tryLogInWithLocalToken()
      .then((account) => {
        if (account) {
          membersStore.changeConnectedAccount(account)
          if (!account.hasAcceptedTerms) {
            setIsCGUPopupOpen(true)
          }
        }
      })
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    setVh() // Set on initial load

    window.addEventListener('resize', setVh) // Set on resize

    // Cleanup function
    return () => {
      window.removeEventListener('resize', setVh) // Remove event listener on unmount
    }
  }, []) // Empty dependency array so effect only runs on mount and unmount

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CircularProgressPanel />
      </ThemeProvider>
    )
  }

  return (
    <MetricalpReactProvider
      initialSharedCustomProps={{
        screen_view: {
          customerId: membersStore.connectedAccount?.customerId ?? 'not-connected',
          customerName: membersStore?.connectedAccount?.id ?? 'not-connected',
        },
      }}
      tid="mam37"
    >
      <ScrollProvider>
        <CssBaseline />

        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100 * var(--vh))',
                maxHeight: 'calc(100 * var(--vh))',
                overflowY: 'hidden',
              }}
            >
              <Routes>
                <Route path="/" element={<PrivateRoutes />}>
                  <Route index element={<Navigate to={PATHS.animals.absolute} />} />

                  <Route path={PATHS.association.relative}>
                    <Route index element={<AssociationDetailsScreen />} />
                    <Route path="completion" element={<CompletionScreen />} />
                    <Route path="template" element={<EditTemplateDocument />} />
                    <Route path="signature" element={<SignatureScreen />} />
                    <Route path="zone" element={<SelectZonesScreen />} />
                    <Route path={PATHS.ajouterDonateur.relative} element={<AddDonatorScreen />} />
                  </Route>

                  <Route path={PATHS.carte.relative}>
                    <Route index element={<MapScreen />} />
                  </Route>

                  <Route path={PATHS.animals.relative}>
                    <Route index element={<AnimalListScreen />} />
                    <Route path={PATHS.ajouterAnimal.relative} element={<AddAnimalScreen />} />
                    <Route path={PATHS.animalDetails.relative} element={<AnimalDetailsScreen />} />
                    <Route path={PATHS.multiEdition.relative} element={<AnimalBulkEdit />} />
                  </Route>

                  <Route path={PATHS.suiviBenevole.relative}>
                    <Route index element={<ContactListScreen />} />
                    <Route path={PATHS.ajouterBenevole.relative} element={<AddMemberScreen />} />
                    <Route path={PATHS.accountDetails.relative} element={<MemberDetailsScreen />} />
                  </Route>

                  <Route path={PATHS.suiviVeterinaire.relative}>
                    <Route index element={<VeterinariesListScreen />} />
                    <Route path={PATHS.ajouterVeterinaire.relative} element={<AddMemberScreen />} />
                    <Route path={PATHS.veterinaryDetails.relative} element={<MemberDetailsScreen />} />
                  </Route>

                  <Route path={PATHS.suiviAdoptant.relative}>
                    <Route index element={<AdoptersListScreen />} />
                    <Route path="email-template" element={<EditTemplateEmail />} />
                    <Route path={PATHS.ajouterAdoptant.relative} element={<AddAdopterScreen />} />
                    <Route path={PATHS.adoptantDetails.relative} element={<AdopterDetailsScreen />} />
                    <Route path={PATHS.assignerAnimal.relative} element={<AssignAnimalToAdopter />} />
                  </Route>
                </Route>
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/admin-login" element={<AdminLoginPage />} />
                {/* Member limits are gone: the former lock screen now leads back to the app. */}
                <Route path="locked" element={<Navigate to="/" />} />
              </Routes>
            </Box>

            <CGUPopUp isOpen={isCGUPopupOpen} close={() => setIsCGUPopupOpen(false)} />
            <PaymentsTutorialModal open={isPaymentsTutorialOpen} onClose={closePaymentsTutorial} />
            <ContentLimitsOfferModal stage={contentLimitsOfferStage} onClose={() => setContentLimitsOfferStage(null)} />

            <GlobalSnackBar />
          </QueryClientProvider>
        </ThemeProvider>
      </ScrollProvider>
    </MetricalpReactProvider>
  )
}

// window.onerror = function (message, url, lineNumber, columnNumber, error) {
//   ErrorsClient.notifyError({ message, url, lineNumber, currentUrl: window.location.href, error }).then(() => {
//     return true
//   })
// }

window.addEventListener('error', (event) => {
  ErrorsClient.notifyError({
    message: event.message,
    currentUrl: window.location.href,
    error: event.error.toString(),
    stack: event.error.stack,
  }).then(() => {
    return true
  })
})
export default App
